import { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Plus, Trash2 } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  useCreateBenchmark,
  useDeleteBenchmark,
  useSaveBenchmarkAssessments,
  useStandardsGradebookQuery,
  useUpdateBenchmark,
  useUpdateStandardsGradebook,
} from './useStandardsGradebook';

interface Props {
  classOfferingId: string;
  canConfigure: boolean;
}

interface ScaleRow {
  code: string;
  label: string;
}

type CellValues = Record<string, string>;

function todayLocalISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const cellKey = (benchmarkId: string, personId: string) => `${benchmarkId}:${personId}`;

function messages(errors?: ReadonlyArray<{ readonly message: string }> | null): string | null {
  return errors?.length ? errors.map((error) => error.message).join(', ') : null;
}

export default function StandardsAssessmentSection({ classOfferingId, canConfigure }: Props) {
  const [date, setDate] = useState(todayLocalISO());
  const [selectedGradebookId, setSelectedGradebookId] = useState<string>('');
  const [values, setValues] = useState<CellValues>({});
  const [initialValues, setInitialValues] = useState<CellValues>({});
  const [scaleRows, setScaleRows] = useState<ScaleRow[]>([]);
  const [benchmarkName, setBenchmarkName] = useState('');
  const [benchmarkDomain, setBenchmarkDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, refetch } = useStandardsGradebookQuery(classOfferingId, date);
  const [updateGradebook] = useUpdateStandardsGradebook();
  const [createBenchmark] = useCreateBenchmark();
  const [updateBenchmark] = useUpdateBenchmark();
  const [deleteBenchmark] = useDeleteBenchmark();
  const [saveAssessments] = useSaveBenchmarkAssessments();

  const offering = data?.classOffering;
  const gradebooks = useMemo(() => offering?.gradebooks ?? [], [offering]);
  const standardsGradebooks = useMemo(
    () => gradebooks.filter((gradebook) => gradebook.gradingMode === 'STANDARDS'),
    [gradebooks]
  );
  const selectedGradebook = standardsGradebooks.find((gradebook) => gradebook.id === selectedGradebookId)
    ?? standardsGradebooks[0];
  const activeBenchmarks = useMemo(
    () => [...(selectedGradebook?.benchmarks ?? [])]
      .filter((benchmark) => benchmark.active)
      .sort((a, b) => a.position - b.position),
    [selectedGradebook]
  );
  const activeStudents = useMemo(
    () => [...(selectedGradebook?.memberships ?? [])]
      .map((membership) => membership.rosterEntry)
      .filter((entry) => entry.state === 'ACTIVE')
      .map((entry) => entry.person)
      .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)),
    [selectedGradebook]
  );

  useEffect(() => {
    if (!selectedGradebookId && standardsGradebooks[0]) {
      setSelectedGradebookId(standardsGradebooks[0].id);
    } else if (selectedGradebookId && !standardsGradebooks.some((book) => book.id === selectedGradebookId)) {
      setSelectedGradebookId(standardsGradebooks[0]?.id ?? '');
    }
  }, [selectedGradebookId, standardsGradebooks]);

  useEffect(() => {
    if (!selectedGradebook) {
      setValues({});
      setInitialValues({});
      setScaleRows([]);
      return;
    }

    const next: CellValues = {};
    selectedGradebook.benchmarks.forEach((benchmark) => {
      benchmark.assessmentsOn.forEach((assessment) => {
        next[cellKey(benchmark.id, assessment.person.id)] = assessment.levelCode;
      });
    });
    setValues(next);
    setInitialValues(next);
    setScaleRows(selectedGradebook.assessmentScale.map((level) => ({ ...level })));
    setError(null);
  }, [selectedGradebook]);

  const changedKeys = useMemo(() => {
    const keys = new Set([...Object.keys(values), ...Object.keys(initialValues)]);
    return [...keys].filter((key) => (values[key] ?? '') !== (initialValues[key] ?? ''));
  }, [values, initialValues]);

  const enableStandards = async (gradebookId: string) => {
    setError(null);
    const result = await updateGradebook({
      variables: { input: { gradebookId, gradingMode: 'STANDARDS' } },
    });
    const nextError = messages(result.data?.updateStandardsGradebook?.userErrors);
    if (nextError) {
      setError(nextError);
      return;
    }
    setSelectedGradebookId(gradebookId);
    await refetch();
  };

  const saveScale = async () => {
    if (!selectedGradebook) return;
    const normalized = scaleRows
      .map((row) => ({ code: row.code.trim().toUpperCase(), label: row.label.trim() }))
      .filter((row) => row.code || row.label);
    setError(null);
    const result = await updateGradebook({
      variables: {
        input: {
          gradebookId: selectedGradebook.id,
          gradingMode: 'STANDARDS',
          assessmentScale: normalized,
        },
      },
    });
    const nextError = messages(result.data?.updateStandardsGradebook?.userErrors);
    if (nextError) {
      setError(nextError);
      return;
    }
    await refetch();
  };

  const addBenchmark = async () => {
    if (!selectedGradebook || !benchmarkName.trim()) return;
    setError(null);
    const result = await createBenchmark({
      variables: {
        input: {
          gradebookId: selectedGradebook.id,
          name: benchmarkName.trim(),
          ...(benchmarkDomain.trim() ? { domain: benchmarkDomain.trim() } : {}),
        },
      },
    });
    const nextError = messages(result.data?.createBenchmark?.userErrors);
    if (nextError) {
      setError(nextError);
      return;
    }
    setBenchmarkName('');
    setBenchmarkDomain('');
    await refetch();
  };

  const archiveBenchmark = async (benchmarkId: string) => {
    setError(null);
    const result = await updateBenchmark({ variables: { input: { id: benchmarkId, active: false } } });
    const nextError = messages(result.data?.updateBenchmark?.userErrors);
    if (nextError) {
      setError(nextError);
      return;
    }
    await refetch();
  };

  const removeBenchmark = async (benchmarkId: string) => {
    if (!window.confirm('Delete this unused benchmark?')) return;
    setError(null);
    const result = await deleteBenchmark({ variables: { input: { id: benchmarkId } } });
    const nextError = messages(result.data?.deleteBenchmark?.userErrors);
    if (nextError) {
      setError(nextError);
      return;
    }
    await refetch();
  };

  const saveGrid = async () => {
    if (!selectedGradebook || changedKeys.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const assessments = changedKeys.map((key) => {
        const [benchmarkId, personId] = key.split(':');
        const levelCode = values[key] ?? '';
        return levelCode
          ? { benchmarkId, personId, levelCode }
          : { benchmarkId, personId, clear: true };
      });
      const result = await saveAssessments({
        variables: { input: { gradebookId: selectedGradebook.id, assessedOn: date, assessments } },
      });
      const nextError = messages(result.data?.saveBenchmarkAssessments?.userErrors);
      if (nextError) {
        setError(nextError);
        return;
      }
      await refetch();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save assessments');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Standards assessments
            </CardTitle>
            <CardDescription>
              Record progress benchmarks without converting younger students to letter grades.
            </CardDescription>
          </div>
          <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="w-auto" />
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading && <p className="text-sm text-muted-foreground">Loading assessments…</p>}

        {!loading && standardsGradebooks.length === 0 && (
          <div className="rounded-lg border border-dashed p-6">
            <p className="text-sm text-muted-foreground">No standards-based gradebook is configured for this class.</p>
            {canConfigure && gradebooks.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {gradebooks.map((gradebook) => (
                  <Button key={gradebook.id} variant="outline" size="sm" onClick={() => enableStandards(gradebook.id)}>
                    Use {gradebook.name} for standards
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedGradebook && (
          <>
            {standardsGradebooks.length > 1 && (
              <div className="max-w-sm space-y-1.5">
                <Label>Gradebook</Label>
                <Select value={selectedGradebook.id} onValueChange={setSelectedGradebookId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {standardsGradebooks.map((gradebook) => (
                      <SelectItem key={gradebook.id} value={gradebook.id}>{gradebook.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {canConfigure && (
              <div className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Assessment scale</h4>
                    <p className="text-xs text-muted-foreground">Codes remain stable once teachers record them.</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setScaleRows((rows) => [...rows, { code: '', label: '' }])}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add level
                  </Button>
                </div>
                {scaleRows.map((row, index) => (
                  <div key={`${row.code}-${index}`} className="grid grid-cols-[1fr_2fr_auto] gap-2">
                    <Input
                      aria-label={`Level ${index + 1} code`}
                      value={row.code}
                      onChange={(event) => setScaleRows((rows) => rows.map((item, itemIndex) => itemIndex === index ? { ...item, code: event.target.value } : item))}
                      placeholder="MEETS"
                    />
                    <Input
                      aria-label={`Level ${index + 1} label`}
                      value={row.label}
                      onChange={(event) => setScaleRows((rows) => rows.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item))}
                      placeholder="Meets expectations"
                    />
                    <Button variant="ghost" size="icon" onClick={() => setScaleRows((rows) => rows.filter((_, itemIndex) => itemIndex !== index))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex justify-end"><Button size="sm" onClick={saveScale}>Save scale</Button></div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="font-medium">Benchmarks</h4>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedGradebook.assessmentScale.map((level) => <Badge key={level.code} variant="outline">{level.label}</Badge>)}
                  </div>
                </div>
              </div>
              {canConfigure && (
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <Input value={benchmarkName} onChange={(event) => setBenchmarkName(event.target.value)} placeholder="Benchmark name" />
                  <Input value={benchmarkDomain} onChange={(event) => setBenchmarkDomain(event.target.value)} placeholder="Domain (optional)" />
                  <Button onClick={addBenchmark} disabled={!benchmarkName.trim()}><Plus className="mr-1 h-4 w-4" /> Add</Button>
                </div>
              )}
            </div>

            {activeBenchmarks.length === 0 || activeStudents.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {activeBenchmarks.length === 0 ? 'Add a benchmark to begin assessing progress.' : 'No active students are available.'}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="sticky left-0 z-10 min-w-48 bg-muted p-3 text-left font-medium">Student</th>
                      {activeBenchmarks.map((benchmark) => (
                        <th key={benchmark.id} className="min-w-48 p-3 text-left align-top font-medium">
                          <div>{benchmark.name}</div>
                          {benchmark.domain && <div className="text-xs font-normal text-muted-foreground">{benchmark.domain}</div>}
                          {canConfigure && (
                            <div className="mt-1 flex gap-1">
                              <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => archiveBenchmark(benchmark.id)}>Archive</Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeBenchmark(benchmark.id)}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeStudents.map((student) => (
                      <tr key={student.id} className="border-b last:border-0">
                        <td className="sticky left-0 bg-background p-3 font-medium">{student.lastName}, {student.firstName}</td>
                        {activeBenchmarks.map((benchmark) => {
                          const key = cellKey(benchmark.id, student.id);
                          return (
                            <td key={benchmark.id} className="p-2">
                              <Select value={values[key] || 'BLANK'} onValueChange={(value) => setValues((current) => ({ ...current, [key]: value === 'BLANK' ? '' : value }))}>
                                <SelectTrigger aria-label={`${student.firstName} ${student.lastName}: ${benchmark.name}`}><SelectValue placeholder="Not assessed" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="BLANK">Not assessed</SelectItem>
                                  {selectedGradebook.assessmentScale.map((level) => <SelectItem key={level.code} value={level.code}>{level.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {changedKeys.length > 0 && (
              <div className="flex justify-end gap-2 border-t pt-3">
                <Button variant="ghost" disabled={saving} onClick={() => setValues(initialValues)}>Discard</Button>
                <Button disabled={saving} onClick={saveGrid}>{saving ? 'Saving…' : 'Save assessments'}</Button>
              </div>
            )}
          </>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
